import { useCallback, useEffect, useMemo, useState } from "react";
import type { Direction, SlotOverview } from "../../shared/types";
import { TickerSearch } from "../components/TickerSearch";
import { Card, ErrorBanner, SectionHeader, Spinner } from "../components/ui";
import { api, setAdminPassword } from "../lib/api";
import { formatMoney } from "../lib/format";

export default function AdminPage() {
  const [authed, setAuthed] = useState<boolean | null>(null);

  useEffect(() => {
    api
      .session()
      .then((s) => setAuthed(s.authed))
      .catch(() => setAuthed(false));
  }, []);

  if (authed === null) return <Spinner label="Checking session…" />;
  if (!authed) return <LoginGate onAuthed={() => setAuthed(true)} />;
  return <AdminConsole onLogout={() => setAuthed(false)} />;
}

function LoginGate({ onAuthed }: { onAuthed: () => void }) {
  const [pw, setPw] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      setAdminPassword(pw);
      await api.login(pw);
      onAuthed();
    } catch (e) {
      setAdminPassword(null);
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto max-w-sm pt-16">
      <Card>
        <SectionHeader title="Commissioner sign-in" />
        <form onSubmit={submit} className="space-y-3">
          <input
            type="password"
            autoFocus
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            placeholder="Commissioner password"
            className="w-full rounded-lg border border-edge bg-ink-900 px-3 py-2.5 text-fg placeholder:text-faint focus:border-accent"
          />
          {err && <ErrorBanner message={err} />}
          <button
            disabled={busy}
            className="w-full rounded-lg bg-accent py-2.5 font-bold text-ink-950 disabled:opacity-50"
          >
            Unlock admin
          </button>
        </form>
      </Card>
      <p className="mt-3 text-center text-xs text-faint">
        Viewers can browse the Season and Draft tabs without signing in.
      </p>
    </div>
  );
}

function AdminConsole({ onLogout }: { onLogout: () => void }) {
  const [slots, setSlots] = useState<SlotOverview[]>([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const reload = useCallback(async () => {
    try {
      const { slots } = await api.slots();
      setSlots(slots);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  const flash = (msg: string) => {
    setToast(msg);
    setErr(null);
    setTimeout(() => setToast(null), 3500);
  };
  const fail = (e: unknown) => setErr(e instanceof Error ? e.message : String(e));

  async function logout() {
    try {
      await api.logout();
    } catch {
      /* ignore */
    }
    setAdminPassword(null);
    onLogout();
  }

  if (loading) return <Spinner label="Loading admin…" />;

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-extrabold text-fg">Commissioner Admin</h1>
        <button
          onClick={logout}
          className="rounded-lg border border-edge px-3 py-1.5 text-sm text-muted hover:text-fg"
        >
          Sign out
        </button>
      </div>

      {toast && (
        <div className="rounded-xl border border-gain/40 bg-gain/10 px-4 py-3 text-sm text-gain">
          {toast}
        </div>
      )}
      {err && <ErrorBanner message={err} />}

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        <DraftControls onDone={flash} onFail={fail} reload={reload} />
        <EditPickForm slots={slots} onDone={flash} onFail={fail} reload={reload} />
        <AddUniverseForm onDone={flash} onFail={fail} />
        <TradeForm slots={slots} onDone={flash} onFail={fail} reload={reload} />
        <RebalanceForm slots={slots} onDone={flash} onFail={fail} reload={reload} />
        <CorporateActionForm slots={slots} onDone={flash} onFail={fail} reload={reload} />
        <DividendsForm onDone={flash} onFail={fail} />
        <UniverseForm onDone={flash} onFail={fail} />
        <UtilityForm onDone={flash} onFail={fail} reload={reload} />
      </div>
    </div>
  );
}

type Handlers = {
  onDone: (m: string) => void;
  onFail: (e: unknown) => void;
  reload: () => Promise<void>;
};

function slotLabel(s: SlotOverview): string {
  const held = s.open ? `${s.open.ticker} ${s.open.direction}` : "empty";
  return `${s.managerName} · S${s.slotIndex} · ${held}`;
}

function DraftControls({ onDone, onFail, reload }: Handlers) {
  const [pick, setPick] = useState("");
  const run = async (fn: () => Promise<unknown>, msg: string) => {
    try {
      await fn();
      await reload();
      onDone(msg);
    } catch (e) {
      onFail(e);
    }
  };
  return (
    <Card>
      <SectionHeader title="Draft controls" />
      <div className="flex flex-wrap gap-2">
        <button
          className="rounded-lg bg-gain px-3 py-2 text-sm font-bold text-ink-950"
          onClick={() => run(() => api.draftControl({ action: "start" }), "Draft started")}
        >
          Start
        </button>
        <button
          className="rounded-lg border border-edge px-3 py-2 text-sm font-semibold text-muted hover:text-fg"
          onClick={() =>
            run(() => api.draftControl({ action: "setStatus", status: "done" }), "Marked complete")
          }
        >
          Mark complete
        </button>
        <button
          className="rounded-lg border border-edge px-3 py-2 text-sm font-semibold text-muted hover:text-fg"
          onClick={() => run(() => api.undo(), "Last pick undone")}
        >
          Undo last pick
        </button>
        <button
          className="rounded-lg border border-loss/50 px-3 py-2 text-sm font-semibold text-lossbright"
          onClick={() => {
            if (confirm("Reset wipes ALL positions, trades and rebalances. Continue?")) {
              run(() => api.draftControl({ action: "reset" }), "Draft reset");
            }
          }}
        >
          Reset draft
        </button>
      </div>
      <div className="mt-3 flex items-center gap-2">
        <input
          value={pick}
          onChange={(e) => setPick(e.target.value)}
          placeholder="pick #"
          inputMode="numeric"
          className="num w-24 rounded-lg border border-edge bg-ink-900 px-3 py-2 text-fg placeholder:text-faint"
        />
        <button
          className="rounded-lg border border-edge px-3 py-2 text-sm font-semibold text-muted hover:text-fg"
          onClick={() =>
            run(
              () => api.draftControl({ action: "setPick", pick: Number(pick) }),
              `Jumped to pick ${pick}`,
            )
          }
        >
          Set current pick
        </button>
      </div>
    </Card>
  );
}

function EditPickForm({
  slots,
  onDone,
  onFail,
  reload,
}: { slots: SlotOverview[] } & Handlers) {
  const [slotId, setSlotId] = useState(0);
  const [ticker, setTicker] = useState("");
  const [direction, setDirection] = useState<Direction>("LONG");
  const [price, setPrice] = useState("");
  const filled = slots.filter((s) => s.open);

  async function submit() {
    try {
      await api.editPick({
        slotId,
        ticker,
        direction,
        price: price ? Number(price) : undefined,
      });
      await reload();
      onDone("Pick updated");
      setTicker("");
      setPrice("");
    } catch (e) {
      onFail(e);
    }
  }

  return (
    <Card>
      <SectionHeader title="Change a pick" />
      <p className="mb-3 text-xs text-muted">
        Swap a slot's current pick for a different name (e.g. replace a
        placeholder). Add the name to the pool first if it isn't there.
      </p>
      <div className="space-y-2">
        <SlotSelect label="Slot to change" slots={filled} value={slotId} onChange={setSlotId} />
        <div className="flex items-center gap-2">
          <DirToggle value={direction} onChange={setDirection} />
          {ticker && <span className="num text-sm font-bold text-fg">{ticker}</span>}
        </div>
        <TickerSearch onSelect={(r) => setTicker(r.ticker)} placeholder="new ticker…" />
        <PriceInput value={price} onChange={setPrice} placeholder="price (optional — live if blank)" />
        <button
          disabled={!slotId || !ticker}
          onClick={submit}
          className="w-full rounded-lg bg-accent py-2 text-sm font-bold text-ink-950 disabled:opacity-40"
        >
          Update pick
        </button>
      </div>
    </Card>
  );
}

function AddUniverseForm({ onDone, onFail }: Pick<Handlers, "onDone" | "onFail">) {
  const [ticker, setTicker] = useState("");
  const [name, setName] = useState("");
  const [sector, setSector] = useState("Top Companies Traded");

  async function submit() {
    try {
      const r = await api.addUniverseName(ticker, name, sector);
      onDone(`Added ${r.ticker} to the pool`);
      setTicker("");
      setName("");
    } catch (e) {
      onFail(e);
    }
  }

  return (
    <Card>
      <SectionHeader title="Add to pool" />
      <p className="mb-3 text-xs text-muted">
        Add a single ticker so it's searchable and pickable in the draft.
      </p>
      <div className="space-y-2">
        <input
          value={ticker}
          onChange={(e) => setTicker(e.target.value)}
          placeholder="ticker (e.g. RBLX)"
          className="num w-full rounded-lg border border-edge bg-ink-900 px-3 py-2 text-fg placeholder:text-faint focus:border-accent"
        />
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="company name"
          className="w-full rounded-lg border border-edge bg-ink-900 px-3 py-2 text-fg placeholder:text-faint focus:border-accent"
        />
        <input
          value={sector}
          onChange={(e) => setSector(e.target.value)}
          placeholder="sector"
          className="w-full rounded-lg border border-edge bg-ink-900 px-3 py-2 text-fg placeholder:text-faint focus:border-accent"
        />
        <button
          disabled={!ticker.trim()}
          onClick={submit}
          className="w-full rounded-lg bg-accent py-2 text-sm font-bold text-ink-950 disabled:opacity-40"
        >
          Add to pool
        </button>
      </div>
    </Card>
  );
}

function TradeForm({ slots, onDone, onFail, reload }: { slots: SlotOverview[] } & Handlers) {
  const [from, setFrom] = useState<number>(0);
  const [to, setTo] = useState<number>(0);
  const [priceFrom, setPriceFrom] = useState("");
  const [priceTo, setPriceTo] = useState("");
  const filled = slots.filter((s) => s.open);

  async function submit() {
    try {
      await api.trade({
        fromSlotId: from,
        toSlotId: to,
        priceFrom: priceFrom ? Number(priceFrom) : undefined,
        priceTo: priceTo ? Number(priceTo) : undefined,
      });
      await reload();
      onDone("Trade executed");
    } catch (e) {
      onFail(e);
    }
  }

  return (
    <Card>
      <SectionHeader title="Execute trade (swap)" />
      <p className="mb-3 text-xs text-muted">
        Swaps the two slots' open positions at trade-date prices. Each slot keeps
        its realized return and opens the counterparty's ticker.
      </p>
      <div className="space-y-2">
        <SlotSelect label="From slot" slots={filled} value={from} onChange={setFrom} />
        <SlotSelect label="To slot" slots={slots} value={to} onChange={setTo} />
        <div className="flex gap-2">
          <PriceInput value={priceFrom} onChange={setPriceFrom} placeholder="from price (opt)" />
          <PriceInput value={priceTo} onChange={setPriceTo} placeholder="to price (opt)" />
        </div>
        <button
          disabled={!from || !to}
          onClick={submit}
          className="w-full rounded-lg bg-accent py-2 text-sm font-bold text-ink-950 disabled:opacity-40"
        >
          Execute trade
        </button>
      </div>
    </Card>
  );
}

function RebalanceForm({ slots, onDone, onFail, reload }: { slots: SlotOverview[] } & Handlers) {
  const managers = useMemo(() => {
    const m = new Map<number, string>();
    slots.forEach((s) => m.set(s.managerId, s.managerName));
    return [...m.entries()].map(([id, name]) => ({ id, name }));
  }, [slots]);
  const [managerId, setManagerId] = useState<number>(managers[0]?.id ?? 0);
  const mySlots = slots.filter((s) => s.managerId === managerId);
  const [discards, setDiscards] = useState<number[]>([]);
  const [pickups, setPickups] = useState<
    { slotId: number; ticker: string; direction: Direction }[]
  >([]);

  const toggleDiscard = (slotId: number) =>
    setDiscards((d) =>
      d.includes(slotId) ? d.filter((x) => x !== slotId) : d.length < 2 ? [...d, slotId] : d,
    );

  async function submit() {
    try {
      await api.rebalance({ managerId, discards, pickups });
      await reload();
      setDiscards([]);
      setPickups([]);
      onDone("Rebalance applied");
    } catch (e) {
      onFail(e);
    }
  }

  return (
    <Card>
      <SectionHeader title="Quarterly rebalance" />
      <select
        value={managerId}
        onChange={(e) => {
          setManagerId(Number(e.target.value));
          setDiscards([]);
          setPickups([]);
        }}
        className="mb-3 w-full rounded-lg border border-edge bg-ink-900 px-3 py-2 text-fg"
      >
        {managers.map((m) => (
          <option key={m.id} value={m.id}>
            {m.name}
          </option>
        ))}
      </select>

      <div className="mb-2 text-xs uppercase tracking-widest text-faint">
        Discard up to 2
      </div>
      <div className="mb-3 flex flex-wrap gap-1.5">
        {mySlots
          .filter((s) => s.open)
          .map((s) => (
            <button
              key={s.slotId}
              onClick={() => toggleDiscard(s.slotId)}
              className={`num rounded-lg px-2 py-1 text-xs font-semibold ${
                discards.includes(s.slotId)
                  ? "bg-loss text-ink-950"
                  : "bg-ink-700 text-muted"
              }`}
            >
              S{s.slotIndex} {s.open?.ticker}
            </button>
          ))}
      </div>

      <div className="mb-2 flex items-center justify-between">
        <span className="text-xs uppercase tracking-widest text-faint">
          Pick up to 2
        </span>
        <button
          disabled={pickups.length >= 2}
          onClick={() =>
            setPickups((p) => [
              ...p,
              { slotId: mySlots[0]?.slotId ?? 0, ticker: "", direction: "LONG" },
            ])
          }
          className="text-xs text-accent disabled:opacity-40"
        >
          + add pickup
        </button>
      </div>
      <div className="space-y-2">
        {pickups.map((pk, i) => (
          <div key={i} className="rounded-lg border border-edge p-2">
            <div className="mb-2 flex items-center gap-2">
              <select
                value={pk.slotId}
                onChange={(e) =>
                  setPickups((p) =>
                    p.map((x, j) => (j === i ? { ...x, slotId: Number(e.target.value) } : x)),
                  )
                }
                className="rounded-lg border border-edge bg-ink-900 px-2 py-1 text-sm text-fg"
              >
                {mySlots.map((s) => (
                  <option key={s.slotId} value={s.slotId}>
                    Slot {s.slotIndex}
                  </option>
                ))}
              </select>
              <DirToggle
                value={pk.direction}
                onChange={(d) =>
                  setPickups((p) => p.map((x, j) => (j === i ? { ...x, direction: d } : x)))
                }
              />
              {pk.ticker && (
                <span className="num text-sm font-bold text-fg">{pk.ticker}</span>
              )}
              <button
                onClick={() => setPickups((p) => p.filter((_, j) => j !== i))}
                className="ml-auto text-faint hover:text-loss"
              >
                ✕
              </button>
            </div>
            <TickerSearch
              onSelect={(r) =>
                setPickups((p) => p.map((x, j) => (j === i ? { ...x, ticker: r.ticker } : x)))
              }
              placeholder="pickup ticker…"
            />
          </div>
        ))}
      </div>

      <button
        disabled={!discards.length && !pickups.length}
        onClick={submit}
        className="mt-3 w-full rounded-lg bg-accent py-2 text-sm font-bold text-ink-950 disabled:opacity-40"
      >
        Apply rebalance
      </button>
    </Card>
  );
}

function CorporateActionForm({
  slots,
  onDone,
  onFail,
  reload,
}: { slots: SlotOverview[] } & Handlers) {
  const filled = slots.filter((s) => s.open);
  const [type, setType] = useState<"mna" | "delisting" | "spinoff">("mna");
  const [slotId, setSlotId] = useState<number>(0);
  const [dealPrice, setDealPrice] = useState("");
  const [replacement, setReplacement] = useState("");
  const [replacementDir, setReplacementDir] = useState<Direction>("LONG");
  const [spin, setSpin] = useState("");

  async function submit() {
    try {
      await api.corporateAction({
        type,
        slotId,
        dealPrice: dealPrice ? Number(dealPrice) : undefined,
        replacementTicker: replacement || undefined,
        replacementDirection: replacement ? replacementDir : undefined,
        spinoffValuePerShare: spin ? Number(spin) : undefined,
      });
      await reload();
      setDealPrice("");
      setReplacement("");
      setSpin("");
      onDone("Corporate action applied");
    } catch (e) {
      onFail(e);
    }
  }

  return (
    <Card>
      <SectionHeader title="Corporate action" />
      <div className="space-y-2">
        <div className="flex gap-2">
          {(["mna", "delisting", "spinoff"] as const).map((t) => (
            <button
              key={t}
              onClick={() => setType(t)}
              className={`rounded-lg px-3 py-1.5 text-sm font-semibold ${
                type === t ? "bg-accent text-ink-950" : "bg-ink-700 text-muted"
              }`}
            >
              {t === "mna" ? "M&A" : t === "delisting" ? "Delist" : "Spinoff"}
            </button>
          ))}
        </div>
        <SlotSelect label="Slot" slots={filled} value={slotId} onChange={setSlotId} />

        {type === "mna" && (
          <>
            <PriceInput value={dealPrice} onChange={setDealPrice} placeholder="deal price" />
            <div className="rounded-lg border border-edge p-2">
              <div className="mb-2 flex items-center gap-2">
                <span className="text-xs text-muted">Replacement (optional)</span>
                <DirToggle value={replacementDir} onChange={setReplacementDir} />
                {replacement && (
                  <span className="num text-sm font-bold text-fg">{replacement}</span>
                )}
              </div>
              <TickerSearch
                onSelect={(r) => setReplacement(r.ticker)}
                placeholder="replacement ticker…"
              />
            </div>
          </>
        )}
        {type === "delisting" && (
          <p className="text-xs text-muted">
            Closes at −100% (LONG) / +100% (SHORT). No replacement.
          </p>
        )}
        {type === "spinoff" && (
          <PriceInput value={spin} onChange={setSpin} placeholder="spinoff value / share" />
        )}

        <button
          disabled={!slotId}
          onClick={submit}
          className="w-full rounded-lg bg-accent py-2 text-sm font-bold text-ink-950 disabled:opacity-40"
        >
          Apply
        </button>
      </div>
    </Card>
  );
}

function UniverseForm({ onDone, onFail }: Pick<Handlers, "onDone" | "onFail">) {
  const [csv, setCsv] = useState("");
  const [replace, setReplace] = useState(true);
  const [busy, setBusy] = useState(false);

  async function submit() {
    setBusy(true);
    try {
      const { count } = await api.reloadUniverse(csv, replace);
      onDone(`Universe reloaded — ${count} tickers`);
      setCsv("");
    } catch (e) {
      onFail(e);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <SectionHeader title="Reload universe" />
      <p className="mb-2 text-xs text-muted">
        Paste CSV: <code className="num">ticker,name,sector</code> (header optional).
      </p>
      <textarea
        value={csv}
        onChange={(e) => setCsv(e.target.value)}
        rows={6}
        placeholder={"AAPL,Apple Inc.,Information Technology\nMSFT,Microsoft,Information Technology"}
        className="num w-full rounded-lg border border-edge bg-ink-900 px-3 py-2 text-xs text-fg placeholder:text-faint"
      />
      <div className="mt-2 flex items-center justify-between">
        <label className="flex items-center gap-2 text-sm text-muted">
          <input
            type="checkbox"
            checked={replace}
            onChange={(e) => setReplace(e.target.checked)}
          />
          Replace existing (deactivate names not in this list)
        </label>
        <button
          disabled={busy || !csv.trim()}
          onClick={submit}
          className="rounded-lg bg-accent px-4 py-2 text-sm font-bold text-ink-950 disabled:opacity-40"
        >
          Upload
        </button>
      </div>
    </Card>
  );
}

function DividendsForm({ onDone, onFail }: Pick<Handlers, "onDone" | "onFail">) {
  const [ticker, setTicker] = useState("");
  const [exDate, setExDate] = useState("");
  const [amount, setAmount] = useState("");
  const [list, setList] = useState<{ ticker: string; exDate: string; amount: number }[]>([]);
  const [status, setStatus] = useState<Awaited<ReturnType<typeof api.dividendStatus>> | null>(
    null,
  );
  const [audit, setAudit] = useState<Awaited<ReturnType<typeof api.audit>> | null>(null);

  const sym = ticker.trim().toUpperCase();

  useEffect(() => {
    api.dividendStatus().then(setStatus, () => setStatus(null));
    api.audit().then(setAudit, () => setAudit(null));
  }, []);
  const checks = Object.entries(status?.tickers ?? {});
  const failed = checks.filter(([, c]) => c.source === null);

  const load = useCallback(async (t: string) => {
    if (!t) {
      setList([]);
      return;
    }
    try {
      const { dividends } = await api.dividends(t);
      setList(dividends);
    } catch {
      setList([]);
    }
  }, []);

  useEffect(() => {
    const id = setTimeout(() => load(sym), 300);
    return () => clearTimeout(id);
  }, [sym, load]);

  async function add() {
    try {
      await api.addDividend(sym, exDate, Number(amount));
      onDone(`Dividend added: ${sym} ${exDate} $${amount}`);
      setExDate("");
      setAmount("");
      await load(sym);
    } catch (e) {
      onFail(e);
    }
  }

  async function remove(d: { ticker: string; exDate: string }) {
    try {
      await api.removeDividend(d.ticker, d.exDate);
      await load(sym);
    } catch (e) {
      onFail(e);
    }
  }

  return (
    <Card>
      <SectionHeader title="Dividends (total return)" />
      <p className="mb-2 text-xs text-muted">
        Per-share cash dividends. Only ex-dates after a position opened count
        toward its return. Longs are credited, shorts debited. Held tickers are
        checked automatically every morning (Yahoo Finance, FMP as backup).
      </p>
      {status && (
        <div className="mb-3 text-xs">
          <p className="text-muted">
            Last full check: {status.lastPass ?? "never"} ·{" "}
            {checks.length - failed.length} of {checks.length} tickers OK
          </p>
          {failed.length > 0 && (
            <ul className="mt-1 space-y-0.5 text-loss">
              {failed.map(([t, c]) => (
                <li key={t}>
                  {t}: lookup failed ({c.error || "unknown error"}). Add its
                  dividends by hand below.
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      <div className="flex flex-wrap items-end gap-2">
        <input
          value={ticker}
          onChange={(e) => setTicker(e.target.value)}
          placeholder="ticker"
          className="num w-28 rounded-lg border border-edge bg-ink-900 px-3 py-2 text-fg placeholder:text-faint"
        />
        <input
          type="date"
          value={exDate}
          onChange={(e) => setExDate(e.target.value)}
          className="num rounded-lg border border-edge bg-ink-900 px-3 py-2 text-fg"
        />
        <input
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          placeholder="$ / share"
          inputMode="decimal"
          className="num w-28 rounded-lg border border-edge bg-ink-900 px-3 py-2 text-fg placeholder:text-faint"
        />
        <button
          disabled={!sym || !exDate || !amount}
          onClick={add}
          className="rounded-lg bg-accent px-4 py-2 text-sm font-bold text-ink-950 disabled:opacity-40"
        >
          Add
        </button>
      </div>
      {audit && (
        <div className="mb-3 border-t border-edge pt-2 text-xs">
          <p className="text-muted">
            Nightly audit: {audit.lastAudit ?? "not run yet"}
            {audit.state
              ? ` · ${audit.state.checked} tickers checked against Yahoo`
              : ""}
          </p>
          {audit.state && audit.state.findings.length > 0 ? (
            <ul className="mt-1 space-y-0.5 text-loss">
              {audit.state.findings.map((f, i) => (
                <li key={i}>
                  {f.ticker} ({f.kind}): {f.detail}
                </li>
              ))}
            </ul>
          ) : (
            audit.state && (
              <p className="mt-1 text-gain">
                No discrepancies — prices and dividends match an independent
                source.
              </p>
            )
          )}
        </div>
      )}
      {list.length > 0 && (
        <ul className="num mt-3 space-y-1 text-sm">
          {list.map((d) => (
            <li
              key={d.exDate}
              className="flex items-center justify-between border-t border-edge pt-1"
            >
              <span className="text-muted">
                {d.exDate} · <span className="text-fg">${d.amount.toFixed(2)}</span>
              </span>
              <button
                onClick={() => remove(d)}
                className="text-faint hover:text-loss"
              >
                ✕
              </button>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function UtilityForm({ onDone, onFail, reload }: Handlers) {
  const [ticker, setTicker] = useState("");
  const [price, setPrice] = useState("");
  const [busy, setBusy] = useState(false);

  async function override() {
    try {
      await api.overridePrice(ticker.toUpperCase(), Number(price));
      await reload();
      onDone(`Price set: ${ticker.toUpperCase()} ${formatMoney(Number(price))}`);
      setTicker("");
      setPrice("");
    } catch (e) {
      onFail(e);
    }
  }

  async function recompute() {
    try {
      await api.recompute();
      onDone("Leaderboard recomputed");
    } catch (e) {
      onFail(e);
    }
  }

  async function testAlert() {
    setBusy(true);
    try {
      const r = await api.testAlert();
      if (r.ok) onDone("Telegram test sent — check the chat.");
      else onFail(new Error(r.error || "Telegram test failed"));
    } catch (e) {
      onFail(e);
    } finally {
      setBusy(false);
    }
  }

  async function testFeed() {
    setBusy(true);
    try {
      const r = await api.providerCheck();
      if (!r.quotes?.ok) {
        onFail(new Error(`Massive quotes failed: ${r.quotes?.error || "no data returned"}`));
        return;
      }
      const px = (r.quotes.sample || [])
        .map((s) => `${s.symbol} ${s.price != null ? "$" + s.price : "—"}`)
        .join("   ");
      const div = r.dividends?.ok
        ? `dividends OK (${r.dividends.count} found for TXN)`
        : `dividends unavailable — ${r.dividends?.error || "?"}`;
      onDone(`Massive feed OK — ${px}  ·  ${div}`);
    } catch (e) {
      onFail(e);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <SectionHeader title="Manual price & recompute" />
      <div className="flex flex-wrap items-end gap-2">
        <input
          value={ticker}
          onChange={(e) => setTicker(e.target.value)}
          placeholder="ticker"
          className="num w-28 rounded-lg border border-edge bg-ink-900 px-3 py-2 text-fg placeholder:text-faint"
        />
        <PriceInput value={price} onChange={setPrice} placeholder="price" />
        <button
          disabled={!ticker || !price}
          onClick={override}
          className="rounded-lg border border-edge px-3 py-2 text-sm font-semibold text-muted hover:text-fg disabled:opacity-40"
        >
          Set price
        </button>
        <button
          onClick={recompute}
          className="ml-auto rounded-lg bg-accent px-3 py-2 text-sm font-bold text-ink-950"
        >
          Recompute
        </button>
      </div>
      <div className="mt-3 flex items-center justify-between border-t border-edge pt-3">
        <span className="text-xs text-muted">
          Verify the live quote and dividend feeds (reads only — writes nothing).
        </span>
        <button
          onClick={testFeed}
          disabled={busy}
          className="rounded-lg border border-gain/50 px-3 py-2 text-sm font-semibold text-gain hover:bg-gain/10 disabled:opacity-40"
        >
          {busy ? "Testing…" : "Test data feed"}
        </button>
      </div>
      <div className="mt-3 flex items-center justify-between border-t border-edge pt-3">
        <span className="text-xs text-muted">
          Telegram alerts fire when a dividend lookup or a closing price fails.
        </span>
        <button
          onClick={testAlert}
          disabled={busy}
          className="rounded-lg border border-edge px-3 py-2 text-sm font-semibold text-muted hover:text-fg disabled:opacity-40"
        >
          {busy ? "Sending…" : "Send test alert"}
        </button>
      </div>
    </Card>
  );
}

// ---- small shared inputs ----

function SlotSelect({
  label,
  slots,
  value,
  onChange,
}: {
  label: string;
  slots: SlotOverview[];
  value: number;
  onChange: (v: number) => void;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs uppercase tracking-widest text-faint">
        {label}
      </span>
      <select
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="num w-full rounded-lg border border-edge bg-ink-900 px-3 py-2 text-sm text-fg"
      >
        <option value={0}>— select —</option>
        {slots.map((s) => (
          <option key={s.slotId} value={s.slotId}>
            {slotLabel(s)}
            {s.open ? ` @ ${formatMoney(s.open.costBasis)}` : ""}
          </option>
        ))}
      </select>
    </label>
  );
}

function PriceInput({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
}) {
  return (
    <input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      inputMode="decimal"
      className="num w-full rounded-lg border border-edge bg-ink-900 px-3 py-2 text-fg placeholder:text-faint focus:border-accent"
    />
  );
}

function DirToggle({
  value,
  onChange,
}: {
  value: Direction;
  onChange: (d: Direction) => void;
}) {
  return (
    <div className="flex overflow-hidden rounded-lg border border-edge text-xs">
      {(["LONG", "SHORT"] as Direction[]).map((d) => (
        <button
          key={d}
          onClick={() => onChange(d)}
          className={`px-2 py-1 font-bold ${
            value === d
              ? d === "LONG"
                ? "bg-gain text-ink-950"
                : "bg-loss text-ink-950"
              : "text-muted"
          }`}
        >
          {d}
        </button>
      ))}
    </div>
  );
}
